import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import React, { createContext, useState, useCallback } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import StyledSnackbar, {
  ButtonProps,
  SnackbarType,
  StyledSnackbarProps,
  colorMap
} from 'src/components/BeaconV2Components/common/StyledSnackbar/StyledSnackbar';
import {
  SnackbarCancel,
  SnackbarCheckmark,
  SnackbarCheckmarkPrimary,
  SnackbarImportant
} from 'src/components/SvgIcons';
import { create } from 'zustand';

interface ShowSnackbarArgs {
  type: SnackbarType;
  /**
   * Content of the snackbar message
   */
  message: React.ReactNode;
  /**
   * How long before the snackbar disappears,
   * in milliseconds. Defaults to 7000ms.
   * Pass `null` to disable auto-dismiss (the snackbar will stay until
   * explicitly closed). Avoid `null` unless the snackbar has its own
   * dismiss affordance, otherwise it can stay on screen indefinitely.
   */
  timeout?: number | null;

  /**
   * Boolean to show a similar styled button within the snackbar. Defaults to false.
   */
  showButton?: boolean;
  /**
   * The props forwarded to the button component
   */
  buttonProps?: ButtonProps;
}

interface SnackbarProviderProps {
  children: React.ReactNode;
}

interface SnackbarContextValue {
  showSnackbar: (args: ShowSnackbarArgs) => void;
  closeSnackbar: () => void;
}

export const SnackbarContext = createContext<SnackbarContextValue>(null);

/**
 * Default auto-dismiss duration (ms) used when a caller does not specify a
 * `timeout`. Centralized so the initial Zustand state, `showSnackbar`'s default,
 * and `closeSnackbar`'s reset all stay in sync and a stale `timeout` can never
 * leak from one snackbar to the next.
 */
const DEFAULT_SNACKBAR_TIMEOUT = 3000;

const iconBySnackbarType: Record<SnackbarType, React.ReactNode> = {
  success: <SnackbarCheckmark style={{ width: '24px' }} />,
  error: <SnackbarCancel style={{ width: '24px' }} />,
  info: <SnackbarImportant style={{ width: '24px' }} />,
  primary: <SnackbarCheckmarkPrimary style={{ width: '24px' }} />
};

interface SnackbarButtonProps {
  onClose: () => void;
  type: SnackbarType;
  buttonProps: ButtonProps;
}

const SnackbarButton = ({ ...rest }: SnackbarButtonProps) => {
  const theme = useStacklineTheme();
  return (
    <Flex marginLeft="40px">
      <SlButton
        onClick={(e) => {
          rest.onClose();
          rest.buttonProps.onClick(e);
        }}
        variant="contained"
        buttonSx={{
          paddingLeft: '12px',
          paddingRight: '12px',
          backgroundColor: hexToRgba(theme.colors[colorMap[rest.type]]),
          border: `1px solid ${hexToRgba(theme.colors[colorMap[rest.type]])}`,
          ':hover': {
            borderColor: hexToRgba(theme.colors[colorMap[rest.type]], 0.6),
            backgroundColor: hexToRgba(theme.colors[colorMap[rest.type]], 0.6)
          }
        }}
        textSx={{
          fontSize: '14px',
          fontWeight: 'bold',
          letterSpacing: 'normal'
        }}
      >
        {rest.buttonProps.text}
      </SlButton>
    </Flex>
  );
};

export const SnackbarWithIcon = ({
  message,
  closeSnackbar,
  ...rest
}: StyledSnackbarProps & { closeSnackbar?: () => void }) => {
  return (
    <StyledSnackbar
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom'
      }}
      sx={{
        // Offset by half the sidebar width (185px / 2 = 92.5px) to center relative to content area
        left: 'calc(50% + 92.5px)',
        transform: 'translateX(-50%)'
      }}
      {...rest}
      message={
        <Flex gap="sm" alignItems="center" justifyContent="center">
          {iconBySnackbarType[rest.type]}
          <span style={{ textAlign: 'center' }}>{message}</span>
          {rest.showButton ? (
            <SnackbarButton onClose={closeSnackbar} type={rest.type} buttonProps={rest.buttonProps} />
          ) : null}
        </Flex>
      }
    />
  );
};

/**
 * Zustand hook for controlling snackbar state. Useful for
 * class components that can't use the other useSnackbar hook
 */
export const useSnackbarState = create<{
  open: boolean;
  message: React.ReactNode;
  type: SnackbarType;
  timeout: number | null;
}>(() => ({
  open: false,
  message: null,
  type: 'success',
  timeout: DEFAULT_SNACKBAR_TIMEOUT
}));
/**
 * Provides an interface for opening a snackbar from anywhere in the app.
 */
export default function SnackbarProvider({ children }: SnackbarProviderProps) {
  const snackbarTimeout = useSnackbarState((state) => state.timeout);
  const snackbarOpen = useSnackbarState((state) => state.open);
  const snackbarMessage = useSnackbarState((state) => state.message);
  const snackbarType = useSnackbarState((state) => state.type);

  // Button Specific State
  const [showSnackbarButton, setShowSnackbarButton] = useState(false);
  const [snackbarButtonProps, setSnackbarButtonProps] = useState<ButtonProps>({
    text: 'Continue'
  });

  const showSnackbar = useCallback(
    ({ message, type, timeout = DEFAULT_SNACKBAR_TIMEOUT, showButton = false, buttonProps = {} }: ShowSnackbarArgs) => {
      useSnackbarState.setState((prev) => ({
        ...prev,
        message,
        type,
        timeout,
        open: true
      }));

      setShowSnackbarButton(showButton);
      setSnackbarButtonProps((defaultProps) => ({ ...defaultProps, ...buttonProps }));
    },
    []
  );

  const closeSnackbar = useCallback(() => {
    useSnackbarState.setState((prev) => ({
      ...prev,
      open: false,
      // Reset to the default so a `timeout: null` (never-dismiss) snackbar can't
      // leak its duration into the next snackbar opened via a direct setState.
      timeout: DEFAULT_SNACKBAR_TIMEOUT
    }));
  }, []);

  return (
    <SnackbarContext.Provider
      value={{
        showSnackbar,
        closeSnackbar
      }}
    >
      {children}
      <SnackbarWithIcon
        open={snackbarOpen}
        autoHideDuration={snackbarTimeout}
        type={snackbarType}
        onClose={(_event, reason) => {
          // Ignore clickaway events - only close on timeout or explicit close
          if (reason === 'clickaway') {
            return;
          }
          closeSnackbar();
        }}
        closeSnackbar={closeSnackbar}
        message={snackbarMessage}
        showButton={showSnackbarButton}
        buttonProps={snackbarButtonProps}
      />
    </SnackbarContext.Provider>
  );
}
